import { useEffect, useMemo, useRef, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {
  createAnalyticsPosterCanvas,
  downloadCanvasPng,
  drawAnalyticsPoster,
  type PosterDay,
} from '@/lib/analyticsPoster'
import { shiftDateKey, sumViewsByDateKey, toDateKey } from '@/lib/trackPageView'
import {Download, Instagram, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import type { PageView, SiteSetting } from '@/types'

function parseDateKey(key: string) {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

function formatLong(key: string) {
  return parseDateKey(key).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function formatShort(key: string) {
  return parseDateKey(key).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
  })
}

function daysBetween(fromKey: string, toKey: string) {
  const out: string[] = []
  let key = fromKey
  while (key <= toKey) {
    out.push(key)
    key = shiftDateKey(key, 1)
  }
  return out
}

function shiftRange(fromKey: string, toKey: string) {
  const keys = daysBetween(fromKey, toKey)
  const length = keys.length
  return {
    prevFrom: shiftDateKey(fromKey, -length),
    prevTo: shiftDateKey(fromKey, -1),
    length,
  }
}

export default function InstagramAnalytics() {
  const today = toDateKey()
  const weekAgo = (() => {
    const d = new Date()
    d.setDate(d.getDate() - 6)
    return toDateKey(d)
  })()

  const [fromDate, setFromDate] = useState(weekAgo)
  const [toDate, setToDate] = useState(today)
  const [records, setRecords] = useState<PageView[]>([])
  const [siteName, setSiteName] = useState('CARLOS FELIPE')
  const [gaId, setGaId] = useState('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [viewsRes, titleRes, gaRes, gaResAlt] = await Promise.all([
          lumi.entities.page_views.list({ sort: { dateKey: -1 }, limit: 400 }),
          lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'ga4_measurement_id' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'ga_measurement_id' }, limit: 1 }),
        ])
        setRecords((viewsRes.list || []) as PageView[])
        const title = ((titleRes.list?.[0] as SiteSetting | undefined)?.value || '').trim()
        setSiteName(title || 'CARLOS FELIPE')
        const ga =
          ((gaRes.list?.[0] as SiteSetting | undefined)?.value || '').trim() ||
          ((gaResAlt.list?.[0] as SiteSetting | undefined)?.value || '').trim()
        setGaId(ga)
      } catch {
        toast.error('Erro ao carregar os dados de visualizações.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const byDay = useMemo(() => sumViewsByDateKey(records), [records])

  const rangeValid = fromDate && toDate && fromDate <= toDate
  const selectedKeys = rangeValid ? daysBetween(fromDate, toDate) : []

  const days: PosterDay[] = selectedKeys.map(key => ({
    dateKey: key,
    label: formatShort(key),
    value: byDay.get(key) || 0,
  }))

  const total = days.reduce((sum, day) => sum + day.value, 0)
  const avg = days.length > 0 ? Math.round(total / days.length) : 0
  const peak = days.reduce(
    (best, day) => (day.value > best.value ? day : best),
    days[0] || { dateKey: today, label: formatShort(today), value: 0 },
  )

  const previous = useMemo(() => {
    if (!rangeValid || selectedKeys.length === 0) return { total: 0 }
    const { prevFrom, prevTo } = shiftRange(fromDate, toDate)
    const prevKeys = daysBetween(prevFrom, prevTo)
    const prevTotal = prevKeys.reduce((sum, key) => sum + (byDay.get(key) || 0), 0)
    return { total: prevTotal }
  }, [byDay, fromDate, toDate, rangeValid, selectedKeys.length])

  const growthPct =
    previous.total > 0
      ? Math.round(((total - previous.total) / previous.total) * 100)
      : total > 0
        ? 100
        : null

  const posterPayload = {
    siteName,
    fromLabel: formatLong(fromDate),
    toLabel: formatLong(toDate),
    days,
    total,
    peakLabel: peak?.label || formatShort(toDate),
    peakValue: peak?.value || 0,
    growthPct,
    avg,
  }

  useEffect(() => {
    if (!canvasRef.current || days.length === 0) return
    drawAnalyticsPoster(canvasRef.current, posterPayload)
  }, [days, total, avg, growthPct, peak, siteName, fromDate, toDate])

  const applyPreset = (daysBack: number) => {
    const end = toDateKey()
    setFromDate(shiftDateKey(end, -(daysBack - 1)))
    setToDate(end)
  }

  const handleDownload = async () => {
    if (!rangeValid || !fromDate || !toDate || fromDate > toDate) {
      toast.error('Escolha um intervalo válido.')
      return
    }
    setDownloading(true)
    try {
      const viewsRes = await lumi.entities.page_views.list({ sort: { dateKey: -1 }, limit: 400 })
      const latest = (viewsRes.list || []) as PageView[]
      setRecords(latest)
      const latestByDay = sumViewsByDateKey(latest)
      const keys = daysBetween(fromDate, toDate)
      const latestDays: PosterDay[] = keys.map(key => ({
        dateKey: key,
        label: formatShort(key),
        value: latestByDay.get(key) || 0,
      }))
      const latestTotal = latestDays.reduce((sum, day) => sum + day.value, 0)
      const latestAvg = latestDays.length > 0 ? Math.round(latestTotal / latestDays.length) : 0
      const latestPeak = latestDays.reduce(
        (best, day) => (day.value > best.value ? day : best),
        latestDays[0] || { dateKey: toDate, label: formatShort(toDate), value: 0 },
      )
      const { prevFrom, prevTo } = shiftRange(fromDate, toDate)
      const prevTotal = daysBetween(prevFrom, prevTo).reduce(
        (sum, key) => sum + (latestByDay.get(key) || 0),
        0,
      )
      const latestGrowth =
        prevTotal > 0
          ? Math.round(((latestTotal - prevTotal) / prevTotal) * 100)
          : latestTotal > 0
            ? 100
            : null
      const canvas = createAnalyticsPosterCanvas()
      drawAnalyticsPoster(canvas, {
        siteName,
        fromLabel: formatLong(fromDate),
        toLabel: formatLong(toDate),
        days: latestDays,
        total: latestTotal,
        peakLabel: latestPeak?.label || formatShort(toDate),
        peakValue: latestPeak?.value || 0,
        growthPct: latestGrowth,
        avg: latestAvg,
      })
      downloadCanvasPng(canvas, `carlos-felipe-analytics-${fromDate}-${toDate}.png`)
      toast.success('Imagem pronta com visitas reais + Booster.')
    } catch {
      toast.error('Não foi possível gerar a imagem.')
    } finally {
      setDownloading(false)
    }
  }

  const maxBar = Math.max(...days.map(d => d.value), 1)

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-red-700">
          CARLOS FELIPE
        </p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">Meu Analytics</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Escolha o período, compare os dias e descarregue um cartaz 1080×1350 pronto a publicar no Instagram. Os números da imagem somam as visitas reais com o Booster.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
          <div className="border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <Instagram size={16} className="text-red-600" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Comparativo do período</h2>
            </div>
          </div>

          <div className="space-y-4 px-5 py-5">
            <div className="flex flex-wrap gap-2">
              {[
                { label: '7 dias', days: 7 },
                { label: '14 dias', days: 14 },
                { label: '30 dias', days: 30 },
              ].map(preset => (
                <button
                  key={preset.days}
                  type="button"
                  onClick={() => applyPreset(preset.days)}
                  className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-red-300 hover:text-red-700 dark:border-zinc-700 dark:text-gray-300"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-medium text-gray-500">
                De
                <input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-red-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                />
              </label>
              <label className="block text-xs font-medium text-gray-500">
                Até
                <input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  max={today}
                  onChange={e => setToDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-red-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl bg-zinc-50 px-3 py-3 dark:bg-zinc-900">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Total</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                  {loading ? '—' : total.toLocaleString('pt-PT')}
                </p>
              </div>
              <div className="rounded-xl bg-zinc-50 px-3 py-3 dark:bg-zinc-900">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Média</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                  {loading ? '—' : avg.toLocaleString('pt-PT')}
                </p>
              </div>
              <div className="rounded-xl bg-zinc-50 px-3 py-3 dark:bg-zinc-900">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Pico</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                  {loading ? '—' : (peak?.value || 0).toLocaleString('pt-PT')}
                </p>
              </div>
              <div className="rounded-xl bg-zinc-50 px-3 py-3 dark:bg-zinc-900">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">vs. anterior</p>
                <p className={`mt-1 text-xl font-bold tabular-nums ${
                  growthPct === null
                    ? 'text-gray-900 dark:text-white'
                    : growthPct >= 0
                      ? 'text-emerald-600'
                      : 'text-red-600'
                }`}>
                  {loading || growthPct === null ? '—' : `${growthPct >= 0 ? '+' : ''}${growthPct}%`}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-zinc-100 p-4 dark:border-zinc-800">
              {loading ? (
                <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                  <Loader2 size={18} className="mr-2 animate-spin" /> A carregar...
                </div>
              ) : days.length === 0 ? (
                <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                  Escolha um intervalo válido.
                </div>
              ) : (
                <div className="flex h-52 items-end gap-1.5 min-w-[420px]">
                  {days.map(day => (
                    <div key={day.dateKey} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                      <span className="text-[10px] tabular-nums text-gray-400">
                        {day.value > 0 ? day.value.toLocaleString('pt-PT') : ''}
                      </span>
                      <div
                        className={`w-full rounded-t-md ${day.value === peak.value && peak.value > 0 ? 'bg-red-600' : 'bg-red-400/80'}`}
                        style={{ height: `${Math.max(6, (day.value / maxBar) * 160)}px` }}
                        title={`${day.label}: ${day.value.toLocaleString('pt-PT')}`}
                      />
                      <span className="text-[9px] text-gray-400">{day.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400">
              {gaId
                ? `Google Analytics ligado (${gaId}). O cartaz usa visitas reais + Booster, o nome CARLOS FELIPE e a marca Google Analytics.`
                : 'O cartaz usa visitas reais + Booster e mostra a marca Google Analytics. Configure o ID G- em Rodapé & SEO para ligar o rastreio real.'}
            </p>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-950 dark:border-zinc-800">
            <div className="aspect-[4/5] bg-black">
              <canvas
                ref={canvasRef}
                width={1080}
                height={1350}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || loading || !rangeValid}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {downloading ? 'A gerar...' : 'Descarregar para Instagram'}
          </button>
          <p className="text-xs leading-relaxed text-gray-400">
            Formato 4:5 (1080×1350), ideal para publicação no feed. Cada barra é o total do dia: visitas reais somadas ao Booster.
          </p>
        </aside>
      </div>
    </div>
  )
}
